import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatCard, fmtDate, fmtDateTime, downloadStoredFile } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { WarehouseMap } from "@/components/WarehouseMap";
import { PhotoReceiveDialog } from "@/components/PhotoReceiveDialog";
import { Plus, Stack, MagnifyingGlass, PencilSimple, TrayArrowDown, Cube, CurrencyDollar, X, Check, Receipt, ClockCounterClockwise, ArrowUp, ArrowDown, Warehouse, MapPin, ArrowsLeftRight, ScanSmiley, GitMerge } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const emptyForm = { name: "", quantity: 0, unit_cost: "", notes: "", warehouse_id: "" };
const emptyWh = { name: "", location: "", contact: "", notes: "" };
const today = () => new Date().toISOString().slice(0, 10);
const emptyLine = () => ({ name: "", quantity: 1, batch: "", expiry: "", price: "" });

export default function Stock() {
  const { isTeam, user } = useAuth();
  const canEdit = user?.role === "admin";
  const showValue = user?.role === "admin"; // cost & value visible to admin only
  const [data, setData] = useState({ items: [], total_value: 0, total_items: 0, total_units: 0 });
  const [receipts, setReceipts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [whFilter, setWhFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);

  const [rcOpen, setRcOpen] = useState(false);
  const [editReceiptId, setEditReceiptId] = useState(null);
  const [qtyEdit, setQtyEdit] = useState(null); // { id, value }
  const [recvEdit, setRecvEdit] = useState(null); // { id, value }
  const [receipt, setReceipt] = useState({ date: today(), reference: "", warehouse_id: "", items: [emptyLine()] });
  const [moveItem, setMoveItem] = useState(null);

  const [whOpen, setWhOpen] = useState(false);
  const [whForm, setWhForm] = useState(emptyWh);
  const [whEditId, setWhEditId] = useState(null);

  const [transferItem, setTransferItem] = useState(null);
  const [mergeSrc, setMergeSrc] = useState(null);
  const [mergeTgt, setMergeTgt] = useState("");
  const [merging, setMerging] = useState(false);
  const dupPairs = useMemo(() => {
    const norm = (n) => (n || "").toLowerCase().replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();
    const sig = (n) => { const t = norm(n); const nums = (t.match(/\d+/g) || []).join(","); const words = t.replace(/\d+/g, "").split(" ").filter((w) => w.length > 2 && !["tablet", "tablets", "tab", "tabs", "mg", "ml", "mcg", "box", "pack", "vial", "vials", "cap", "caps", "capsule", "capsules", "the", "and"].includes(w)); return { first: words[0] || t.split(" ")[0] || "", nums }; };
    const items = (data.items || []).filter((x) => x.name);
    const pairs = [];
    for (let i = 0; i < items.length; i++) for (let j = i + 1; j < items.length; j++) {
      const a = items[i], b = items[j];
      if (norm(a.name) === norm(b.name) && a.warehouse_id !== b.warehouse_id) continue;
      const sa = sig(a.name), sb = sig(b.name);
      if (sa.first && sa.first === sb.first && sa.nums === sb.nums && norm(a.name) !== norm(b.name)) pairs.push([a, b]);
      else if (norm(a.name) === norm(b.name)) pairs.push([a, b]);
    }
    return pairs;
  }, [data.items]);
  const doMerge = async () => {
    if (!mergeTgt) return toast.error("Choose the item to merge into");
    setMerging(true);
    try {
      const { data: r } = await api.post("/stock/merge", { source_id: mergeSrc.id, target_id: mergeTgt });
      toast.success(`Merged ${r.merged_quantity} units into ${r.target.name}`);
      setMergeSrc(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Merge failed"); } finally { setMerging(false); }
  };
  const [transferTo, setTransferTo] = useState("");
  const [transferQty, setTransferQty] = useState("");

  const load = useCallback(() => {
    Promise.all([
      api.get("/stock").then((r) => setData(r.data)).catch(() => {}),
      api.get("/stock/receipts").then((r) => setReceipts(r.data || [])).catch(() => {}),
      api.get("/warehouses").then((r) => setWarehouses(r.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const byWh = whFilter === "all" ? data.items : data.items.filter((it) => it.warehouse_id === whFilter);
  const shown = q ? byWh.filter((it) => (it.name || "").toLowerCase().includes(q)) : byWh;

  const openAdd = () => { setForm({ ...emptyForm, warehouse_id: warehouses[0]?.id || "" }); setEditId(null); setAddOpen(true); };
  const openEdit = (it) => {
    setForm({ name: it.name, quantity: it.quantity, unit_cost: it.unit_cost_source === "manual" ? it.unit_cost ?? "" : "", notes: it.notes || "", warehouse_id: it.warehouse_id || "" });
    setEditId(it.id);
    setAddOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Item name is required");
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        quantity: Number(form.quantity) || 0,
        unit_cost: form.unit_cost === "" ? null : Number(form.unit_cost),
        notes: form.notes,
        warehouse_id: form.warehouse_id || null,
      };
      if (editId) {
        await api.patch(`/stock/${editId}`, payload);
        toast.success("Stock item updated");
      } else {
        const { data: res } = await api.post("/stock", payload);
        toast.success(res?.merged ? "Merged into existing item" : "Stock item added");
      }
      setAddOpen(false); setForm(emptyForm); setEditId(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  // ---- Goods receipt (purchase invoice) ----
  const openReceipt = () => { setEditReceiptId(null); setReceipt({ date: today(), reference: "", warehouse_id: warehouses[0]?.id || "", items: [emptyLine()] }); setRcOpen(true); };
  const openEditReceipt = (r) => {
    setEditReceiptId(r.id);
    setReceipt({
      date: r.date || today(), reference: r.reference || "", warehouse_id: r.warehouse_id || "",
      items: (r.items || []).map((it) => ({ name: it.name || "", quantity: it.quantity ?? 1, batch: it.batch || "", expiry: it.expiry || "", price: it.price ?? "" })),
    });
    setRcOpen(true);
  };
  const setLine = (i, patch) => setReceipt((r) => ({ ...r, items: r.items.map((ln, idx) => (idx === i ? { ...ln, ...patch } : ln)) }));
  const addLine = () => setReceipt((r) => ({ ...r, items: [...r.items, emptyLine()] }));
  const removeLine = (i) => setReceipt((r) => ({ ...r, items: r.items.length > 1 ? r.items.filter((_, idx) => idx !== i) : r.items }));

  const saveReceipt = async () => {
    const items = receipt.items.filter((ln) => ln.name.trim() && Number(ln.quantity) > 0);
    if (items.length === 0) return toast.error("Add at least one item with a quantity");
    setBusy(true);
    try {
      const payload = {
        date: receipt.date || today(),
        reference: receipt.reference,
        warehouse_id: receipt.warehouse_id || null,
        items: items.map((ln) => ({ name: ln.name.trim(), quantity: Number(ln.quantity), batch: ln.batch, expiry: ln.expiry, price: ln.price === "" || ln.price == null ? null : Number(ln.price) })),
      };
      if (editReceiptId) {
        await api.patch(`/stock/receipts/${editReceiptId}`, payload);
        toast.success("Goods receipt updated & stock adjusted");
      } else {
        await api.post("/stock/receipts", payload);
        toast.success("Goods receipt saved & stock updated");
      }
      setRcOpen(false); setEditReceiptId(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const saveQty = async (it) => {
    const val = Number(qtyEdit?.value);
    if (Number.isNaN(val) || val < 0) return toast.error("Enter a valid quantity");
    if (val === it.quantity) { setQtyEdit(null); return; }
    setBusy(true);
    try {
      await api.patch(`/stock/${it.id}`, { quantity: val });
      toast.success(`${it.name} quantity updated to ${val}`);
      setQtyEdit(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const saveReceived = async (it) => {
    const val = Number(recvEdit?.value);
    if (Number.isNaN(val) || val < 0) return toast.error("Enter a valid received quantity");
    if (val === (it.received || 0)) { setRecvEdit(null); return; }
    setBusy(true);
    try {
      await api.patch(`/stock/${it.id}/received`, { received: val });
      toast.success(`${it.name} received total updated to ${val}`);
      setRecvEdit(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const remove = async (it) => { await api.delete(`/stock/${it.id}`); toast.success(`${it.name} removed`); load(); };
  const deleteReceipt = async (r) => { await api.delete(`/stock/receipts/${r.id}`); toast.success(`${r.receipt_number} deleted`); load(); };

  // ---- Warehouses ----
  const openWhAdd = () => { setWhForm(emptyWh); setWhEditId(null); setWhOpen(true); };
  const openWhEdit = (w) => { setWhForm({ name: w.name, location: w.location || "", contact: w.contact || "", notes: w.notes || "" }); setWhEditId(w.id); setWhOpen(true); };
  const saveWh = async () => {
    if (!whForm.name.trim()) return toast.error("Warehouse name is required");
    setBusy(true);
    try {
      if (whEditId) { await api.patch(`/warehouses/${whEditId}`, whForm); toast.success("Warehouse updated"); }
      else { await api.post("/warehouses", whForm); toast.success("Warehouse created"); }
      setWhOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  const removeWh = async (w) => {
    try { await api.delete(`/warehouses/${w.id}`); toast.success(`${w.name} deleted`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openTransfer = (it) => { setTransferItem(it); setTransferTo(""); setTransferQty(String(it.quantity ?? "")); };
  const doTransfer = async () => {
    if (!transferTo) return toast.error("Choose a destination warehouse");
    const onHand = Number(transferItem.quantity) || 0;
    const qty = Number(transferQty);
    if (!qty || qty < 1) return toast.error("Enter a quantity to move");
    if (qty > onHand) return toast.error(`Cannot move more than ${onHand} unit(s) on hand`);
    setBusy(true);
    try {
      await api.post(`/stock/${transferItem.id}/transfer`, { warehouse_id: transferTo, quantity: qty });
      toast.success(`Moved ${qty} × ${transferItem.name}`);
      setTransferItem(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        subtitle="Inventory"
        title="Stock List"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setPhotoOpen(true)} data-testid="receive-from-photos-btn">
              <ScanSmiley size={16} className="mr-2" /> Receive from Photos
            </Button>
            <Button variant="outline" className="rounded-sm" onClick={openReceipt} data-testid="new-receipt-btn">
              <TrayArrowDown size={16} className="mr-2" /> Record Received Goods
            </Button>
            {canEdit && (
              <Button className="rounded-sm" onClick={openAdd} data-testid="add-stock-btn"><Plus size={16} className="mr-2" /> Add Item</Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 border border-border rounded-md overflow-hidden bg-card">
        <StatCard label="Items tracked" value={data.total_items} icon={Stack} tone="blue" testid="stock-stat-items" />
        <StatCard label="Total units" value={data.total_units} icon={Cube} tone="zinc" testid="stock-stat-units" />
        <StatCard label="Warehouses" value={warehouses.length} icon={Warehouse} tone="amber" testid="stock-stat-warehouses" />
        {showValue && <StatCard label="Stock value" value={money(data.total_value)} icon={CurrencyDollar} tone="green" testid="stock-stat-value" />}
      </div>

      {canEdit && dupPairs.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 space-y-2" data-testid="duplicate-finder">
          <p className="text-sm font-semibold text-amber-900 flex items-center gap-2"><GitMerge size={16} /> Possible duplicate SKUs ({dupPairs.length})</p>
          <div className="grid md:grid-cols-2 gap-2">
            {dupPairs.map(([a, b], i) => (
              <div key={`${a.id}-${b.id}`} className="flex items-center justify-between gap-2 rounded-sm bg-white/70 border border-amber-200 px-3 py-2 text-sm" data-testid={`dup-pair-${i}`}>
                <div className="min-w-0"><p className="truncate"><strong>{a.name}</strong> <span className="text-muted-foreground">({a.quantity}{a.warehouse_name ? ` · ${a.warehouse_name}` : ""})</span></p><p className="truncate"><strong>{b.name}</strong> <span className="text-muted-foreground">({b.quantity}{b.warehouse_name ? ` · ${b.warehouse_name}` : ""})</span></p></div>
                <Button size="sm" variant="outline" className="rounded-sm shrink-0" onClick={() => { setMergeSrc(b); setMergeTgt(a.id); }} data-testid={`dup-merge-${i}`}><GitMerge size={14} className="mr-1" /> Merge</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Tabs defaultValue="stock" className="w-full">
        <TabsList data-testid="stock-tabs">
          <TabsTrigger value="stock" data-testid="tab-stock">Stock List</TabsTrigger>
          <TabsTrigger value="receipts" data-testid="tab-receipts">Received Goods {receipts.length > 0 && <span className="ml-1.5 text-xs text-muted-foreground">({receipts.length})</span>}</TabsTrigger>
          <TabsTrigger value="warehouses" data-testid="tab-warehouses">Warehouses {warehouses.length > 0 && <span className="ml-1.5 text-xs text-muted-foreground">({warehouses.length})</span>}</TabsTrigger>
        </TabsList>

        {/* ---- Stock list tab ---- */}
        <TabsContent value="stock" className="space-y-4 mt-4">
          {data.items.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative max-w-md flex-1 min-w-[200px]">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input className="pl-9 rounded-sm" placeholder="Search items…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="stock-search" />
              </div>
              <Select value={whFilter} onValueChange={setWhFilter}>
                <SelectTrigger className="w-[220px] rounded-sm" data-testid="stock-warehouse-filter">
                  <SelectValue placeholder="All warehouses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="wh-filter-all">All warehouses</SelectItem>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id} data-testid={`wh-filter-${w.id}`}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {!loading && data.items.length === 0 ? (
            <EmptyState
              title="No stock yet"
              hint="Use Record Received Goods to log incoming items with batch and expiry."
              action={canEdit && <Button className="rounded-sm" onClick={openAdd}>Add Item</Button>}
            />
          ) : (
            <div className="hp-card overflow-x-auto">
              <table className="w-full text-sm" data-testid="stock-table">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-3">Item</th>
                    <th className="px-4 py-3">Warehouse</th>
                    <th className="px-4 py-3 text-right">Received</th>
                    <th className="px-4 py-3 text-right">Sold</th>
                    <th className="px-4 py-3 text-right">On hand</th>
                    {showValue && <th className="px-4 py-3 text-right">Unit cost</th>}
                    {showValue && <th className="px-4 py-3 text-right">Value</th>}
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((it) => {
                    const nextExpiry = (it.batches || []).map((b) => b.expiry).filter(Boolean).sort()[0];
                    return (
                      <tr key={it.id} className="border-b border-border/60 last:border-0" data-testid={`stock-row-${it.name}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium">{it.name}</p>
                          {it.notes && <p className="text-xs text-muted-foreground mt-0.5">{it.notes}</p>}
                          {nextExpiry && <p className="text-xs text-amber-600 mt-0.5">Earliest expiry: {fmtDate(nextExpiry)}</p>}
                        </td>
                        <td className="px-4 py-3">
                          {it.warehouse_name ? (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-200" data-testid={`stock-wh-${it.name}`}>
                              <Warehouse size={12} /> {it.warehouse_name}
                            </span>
                          ) : <span className="text-muted-foreground text-xs">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right text-emerald-700">
                          {canEdit && recvEdit?.id === it.id ? (
                            <div className="flex items-center justify-end gap-1">
                              <Input type="number" min="0" autoFocus className="h-8 w-20 rounded-sm text-right" value={recvEdit.value}
                                onChange={(e) => setRecvEdit({ id: it.id, value: e.target.value })}
                                onKeyDown={(e) => { if (e.key === "Enter") saveReceived(it); if (e.key === "Escape") setRecvEdit(null); }}
                                data-testid={`stock-received-input-${it.name}`} />
                              <Button size="sm" className="rounded-sm h-8 px-2" onClick={() => saveReceived(it)} disabled={busy} data-testid={`stock-received-save-${it.name}`}><Check size={14} /></Button>
                              <Button size="sm" variant="ghost" className="rounded-sm h-8 px-2" onClick={() => setRecvEdit(null)} data-testid={`stock-received-cancel-${it.name}`}><X size={14} /></Button>
                            </div>
                          ) : (
                            <button type="button" disabled={!canEdit} onClick={() => canEdit && setRecvEdit({ id: it.id, value: String(it.received || 0) })}
                              className={`inline-flex items-center gap-1 ${canEdit ? "hover:underline decoration-dotted cursor-pointer" : "cursor-default"}`}
                              title={canEdit ? "Click to adjust received total" : ""} data-testid={`stock-received-${it.name}`}>
                              {it.received || 0}{canEdit && <PencilSimple size={11} className="text-muted-foreground" />}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-blue-700" data-testid={`stock-sold-${it.name}`}>{it.sold || 0}</td>
                        <td className="px-4 py-3 text-right">
                          {canEdit && qtyEdit?.id === it.id ? (
                            <div className="flex items-center justify-end gap-1">
                              <Input type="number" min="0" autoFocus className="h-8 w-20 rounded-sm text-right" value={qtyEdit.value}
                                onChange={(e) => setQtyEdit({ id: it.id, value: e.target.value })}
                                onKeyDown={(e) => { if (e.key === "Enter") saveQty(it); if (e.key === "Escape") setQtyEdit(null); }}
                                data-testid={`stock-qty-input-${it.name}`} />
                              <Button size="sm" className="rounded-sm h-8 px-2" onClick={() => saveQty(it)} disabled={busy} data-testid={`stock-qty-save-${it.name}`}><Check size={14} /></Button>
                              <Button size="sm" variant="ghost" className="rounded-sm h-8 px-2" onClick={() => setQtyEdit(null)} data-testid={`stock-qty-cancel-${it.name}`}><X size={14} /></Button>
                            </div>
                          ) : (
                            <button type="button" disabled={!canEdit} onClick={() => canEdit && setQtyEdit({ id: it.id, value: String(it.quantity) })}
                              className={`inline-flex items-center gap-1 font-semibold ${it.quantity <= 0 ? "text-red-600" : ""} ${canEdit ? "hover:underline decoration-dotted cursor-pointer" : "cursor-default"}`}
                              title={canEdit ? "Click to adjust quantity" : ""} data-testid={`stock-qty-${it.name}`}>
                              {it.quantity}{canEdit && <PencilSimple size={11} className="text-muted-foreground" />}
                            </button>
                          )}
                        </td>
                        {showValue && (
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            {it.unit_cost != null ? (
                              <span>{money(it.unit_cost)}{" "}
                                <span className={`text-[10px] uppercase px-1 py-0.5 rounded-sm border ${it.unit_cost_source === "po" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-zinc-100 text-zinc-600 border-zinc-200"}`}>{it.unit_cost_source}</span>
                              </span>
                            ) : <span className="text-muted-foreground">—</span>}
                          </td>
                        )}
                        {showValue && <td className="px-4 py-3 text-right font-semibold" data-testid={`stock-value-${it.name}`}>{money(it.value)}</td>}
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setMoveItem(it)} data-testid={`movements-stock-${it.name}`} title="Movements"><ClockCounterClockwise size={14} /></Button>
                            {canEdit && <Button variant="outline" size="sm" className="rounded-sm" onClick={() => openTransfer(it)} data-testid={`transfer-stock-${it.name}`} title="Move to another warehouse"><ArrowsLeftRight size={14} /></Button>}
                            {canEdit && <Button variant="outline" size="sm" className="rounded-sm" onClick={() => { setMergeSrc(it); setMergeTgt(""); }} data-testid={`merge-stock-${it.name}`} title="Merge into another item (duplicate SKU)"><GitMerge size={14} /></Button>}
                            {canEdit && <Button variant="outline" size="sm" className="rounded-sm" onClick={() => openEdit(it)} data-testid={`edit-stock-${it.name}`}><PencilSimple size={14} /></Button>}
                            {canEdit && (
                              <ConfirmDelete
                                testid={`delete-stock-${it.name}`}
                                title={`Remove ${it.name}?`}
                                description="This permanently removes the item from the stock list."
                                onConfirm={() => remove(it)}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {shown.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground" data-testid="stock-no-results">No items match your filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ---- Received goods tab ---- */}
        <TabsContent value="receipts" className="space-y-4 mt-4">
          {!loading && receipts.length === 0 ? (
            <EmptyState
              title="No goods receipts yet"
              hint="Record incoming goods as a receipt with date, items, quantities, batch and expiry."
              action={<Button className="rounded-sm" onClick={openReceipt}>Record Received Goods</Button>}
            />
          ) : (
            <div className="space-y-3" data-testid="receipts-list">
              {receipts.map((r) => (
                <div key={r.id} className="hp-card overflow-hidden" data-testid={`receipt-${r.receipt_number}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-border bg-muted/30">
                    <div className="flex items-center gap-2">
                      <Receipt size={18} className="text-primary" />
                      <span className="font-heading font-bold">{r.receipt_number}</span>
                      <span className="text-sm text-muted-foreground">· {fmtDate(r.date)}</span>
                      {r.reference && <span className="text-sm text-muted-foreground">· Ref {r.reference}</span>}
                      {r.warehouse_name && <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-200"><Warehouse size={12} /> {r.warehouse_name}</span>}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground">{r.total_units} unit(s) · by {r.created_by}</span>
                      {canEdit && (
                        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => openEditReceipt(r)} data-testid={`edit-receipt-${r.receipt_number}`}><PencilSimple size={14} className="mr-1" /> Edit</Button>
                      )}
                      {canEdit && (
                        <ConfirmDelete
                          testid={`delete-receipt-${r.receipt_number}`}
                          title={`Delete ${r.receipt_number}?`}
                          description="This removes the receipt record only. It does NOT reduce stock quantities already added."
                          onConfirm={() => deleteReceipt(r)}
                        />
                      )}
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border/60">
                          <th className="px-4 py-2">Item</th>
                          <th className="px-4 py-2 text-right">Qty</th>
                          <th className="px-4 py-2">Batch</th>
                          <th className="px-4 py-2">Expiry</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.items.map((it, i) => (
                          <tr key={i} className="border-b border-border/40 last:border-0">
                            <td className="px-4 py-2 font-medium">{it.name}</td>
                            <td className="px-4 py-2 text-right">{it.quantity}</td>
                            <td className="px-4 py-2 text-muted-foreground">{it.batch || "—"}</td>
                            <td className="px-4 py-2 text-muted-foreground">{it.expiry ? fmtDate(it.expiry) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ---- Warehouses tab ---- */}
        <TabsContent value="warehouses" className="space-y-4 mt-4">
          <div className="flex justify-end">
            {canEdit && <Button className="rounded-sm" onClick={openWhAdd} data-testid="add-warehouse-btn"><Plus size={16} className="mr-2" /> Add Warehouse</Button>}
          </div>
          {!loading && warehouses.length === 0 ? (
            <EmptyState title="No warehouses yet" hint="Create a warehouse profile to organise where your stock is held." action={canEdit && <Button className="rounded-sm" onClick={openWhAdd}>Add Warehouse</Button>} />
          ) : (
            <>
              <WarehouseMap warehouses={warehouses} />
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="warehouses-list">
              {warehouses.map((w) => (
                <div key={w.id} className="hp-card p-4 flex flex-col gap-2" data-testid={`warehouse-card-${w.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Warehouse size={18} className="text-amber-600" />
                      <span className="font-heading font-bold">{w.name}</span>
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-1.5">
                        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => openWhEdit(w)} data-testid={`edit-warehouse-${w.id}`}><PencilSimple size={14} /></Button>
                        <ConfirmDelete
                          testid={`delete-warehouse-${w.id}`}
                          title={`Delete ${w.name}?`}
                          description="Warehouses that still hold stock cannot be deleted. Move their items first."
                          onConfirm={() => removeWh(w)}
                        />
                      </div>
                    )}
                  </div>
                  {w.location && <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin size={14} /> {w.location}</p>}
                  {w.contact && <p className="text-xs text-muted-foreground">Contact: {w.contact}</p>}
                  {w.notes && <p className="text-xs text-muted-foreground">{w.notes}</p>}
                  <div className="mt-auto pt-2 flex items-center gap-4 text-xs text-muted-foreground border-t border-border/60">
                    <span data-testid={`warehouse-items-${w.id}`}><b className="text-foreground">{w.item_count || 0}</b> items</span>
                    <span data-testid={`warehouse-units-${w.id}`}><b className="text-foreground">{w.unit_count || 0}</b> units</span>
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Add / Edit dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="stock-form-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Stock Item" : "Add Stock Item"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">Item name *</Label>
              <Input className="mt-1.5 rounded-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Opdivo 100 mg" data-testid="stock-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Warehouse</Label>
              <Select value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="stock-warehouse-select"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (<SelectItem key={w.id} value={w.id} data-testid={`stock-wh-option-${w.id}`}>{w.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide">Quantity</Label>
                <Input type="number" min="0" className="mt-1.5 rounded-sm" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="stock-qty-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide">Unit cost (optional)</Label>
                <Input type="number" min="0" step="0.01" className="mt-1.5 rounded-sm" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} placeholder="Auto from PO" data-testid="stock-cost-input" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Leave unit cost blank to use the latest purchase-order price automatically.</p>
            <div>
              <Label className="text-xs uppercase tracking-wide">Note (optional)</Label>
              <Textarea rows={2} className="mt-1.5 rounded-sm" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="stock-notes-input" />
            </div>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={save} disabled={busy} data-testid="save-stock-btn">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Goods receipt dialog */}
      <Dialog open={!!mergeSrc} onOpenChange={(o) => !o && setMergeSrc(null)}>
        <DialogContent data-testid="merge-stock-dialog">
          <DialogHeader><DialogTitle>Merge duplicate SKU</DialogTitle></DialogHeader>
          {mergeSrc && (
            <div className="space-y-3 text-sm">
              <p><span className="text-muted-foreground">Merge</span> <strong>{mergeSrc.name}</strong> ({mergeSrc.quantity} on hand{mergeSrc.warehouse_name ? ` · ${mergeSrc.warehouse_name}` : ""}) <span className="text-muted-foreground">into:</span></p>
              <select value={mergeTgt} onChange={(e) => setMergeTgt(e.target.value)} className="h-10 w-full rounded-sm border border-input bg-background px-2 text-sm" data-testid="merge-target-select">
                <option value="">Select the item to keep…</option>
                {[...data.items].filter((x) => x.id !== mergeSrc.id).sort((a, b) => (b.name.toLowerCase().includes(mergeSrc.name.toLowerCase().split(" ")[0]) ? 1 : 0) - (a.name.toLowerCase().includes(mergeSrc.name.toLowerCase().split(" ")[0]) ? 1 : 0)).map((x) => (
                  <option key={x.id} value={x.id}>{x.name} — {x.quantity} on hand{x.warehouse_name ? ` · ${x.warehouse_name}` : ""}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">Quantities, batches and movement history move to the item you keep; <strong>{mergeSrc.name}</strong> is removed from the list. This cannot be undone.</p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setMergeSrc(null)} data-testid="merge-cancel">Cancel</Button>
            <Button className="rounded-sm" onClick={doMerge} disabled={merging || !mergeTgt} data-testid="merge-confirm"><GitMerge size={16} className="mr-2" /> {merging ? "Merging…" : "Merge items"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PhotoReceiveDialog open={photoOpen} onClose={() => setPhotoOpen(false)} warehouses={warehouses} onSaved={load} />
      <Dialog open={rcOpen} onOpenChange={setRcOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="receipt-dialog">
          <DialogHeader><DialogTitle>{editReceiptId ? "Edit Received Goods" : "Record Received Goods"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide">Date</Label>
                <Input type="date" className="mt-1.5 rounded-sm" value={receipt.date} onChange={(e) => setReceipt({ ...receipt, date: e.target.value })} data-testid="receipt-date-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide">Reference / Invoice # (optional)</Label>
                <Input className="mt-1.5 rounded-sm" value={receipt.reference} onChange={(e) => setReceipt({ ...receipt, reference: e.target.value })} placeholder="Supplier invoice no." data-testid="receipt-reference-input" />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide">Receive into warehouse *</Label>
              <Select value={receipt.warehouse_id} onValueChange={(v) => setReceipt({ ...receipt, warehouse_id: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="receipt-warehouse-select"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (<SelectItem key={w.id} value={w.id} data-testid={`receipt-wh-option-${w.id}`}>{w.name}</SelectItem>))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Received items are placed in this warehouse (existing items are moved here).</p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide">Items</Label>
              {receipt.items.map((ln, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end border border-border rounded-sm p-2" data-testid={`receipt-line-${i}`}>
                  <div className="col-span-12 sm:col-span-3">
                    <Label className="text-[10px] uppercase text-muted-foreground">Item *</Label>
                    <Input className="mt-1 rounded-sm" value={ln.name} onChange={(e) => setLine(i, { name: e.target.value })} placeholder="Opdivo 100 mg" data-testid={`receipt-name-${i}`} />
                  </div>
                  <div className="col-span-3 sm:col-span-2">
                    <Label className="text-[10px] uppercase text-muted-foreground">Qty *</Label>
                    <Input type="number" min="1" className="mt-1 rounded-sm" value={ln.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} data-testid={`receipt-qty-${i}`} />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Label className="text-[10px] uppercase text-muted-foreground">Unit price</Label>
                    <Input type="number" min="0" step="0.01" className="mt-1 rounded-sm" value={ln.price} onChange={(e) => setLine(i, { price: e.target.value })} placeholder="Optional" data-testid={`receipt-price-${i}`} />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Label className="text-[10px] uppercase text-muted-foreground">Batch</Label>
                    <Input className="mt-1 rounded-sm" value={ln.batch} onChange={(e) => setLine(i, { batch: e.target.value })} placeholder="Optional" data-testid={`receipt-batch-${i}`} />
                  </div>
                  <div className="col-span-7 sm:col-span-2">
                    <Label className="text-[10px] uppercase text-muted-foreground">Expiry</Label>
                    <Input type="date" className="mt-1 rounded-sm" value={ln.expiry} onChange={(e) => setLine(i, { expiry: e.target.value })} data-testid={`receipt-expiry-${i}`} />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button variant="ghost" size="sm" className="rounded-sm text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => removeLine(i)} disabled={receipt.items.length === 1} data-testid={`receipt-remove-line-${i}`}><X size={16} /></Button>
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" className="rounded-sm" onClick={addLine} data-testid="receipt-add-line"><Plus size={14} className="mr-1.5" /> Add another item</Button>
            </div>
            <p className="text-xs text-muted-foreground">Batch and expiry are optional. Items are matched to existing stock automatically (e.g. "Opdivo 100 mg 1 vial" tops up "Opdivo 100 mg").</p>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={saveReceipt} disabled={busy} data-testid="save-receipt-btn">{busy ? "Saving…" : (editReceiptId ? "Update Receipt & Adjust Stock" : "Save Receipt & Update Stock")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warehouse add/edit dialog */}
      <Dialog open={whOpen} onOpenChange={setWhOpen}>
        <DialogContent data-testid="warehouse-form-dialog">
          <DialogHeader><DialogTitle>{whEditId ? "Edit Warehouse" : "Add Warehouse"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide">Name *</Label>
              <Input className="mt-1.5 rounded-sm" value={whForm.name} onChange={(e) => setWhForm({ ...whForm, name: e.target.value })} placeholder="Cairo Main Store" data-testid="warehouse-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Location</Label>
              <Input className="mt-1.5 rounded-sm" value={whForm.location} onChange={(e) => setWhForm({ ...whForm, location: e.target.value })} placeholder="Nasr City, Cairo, Egypt" data-testid="warehouse-location-input" />
              <p className="text-xs text-muted-foreground mt-1">We'll auto-locate this on the map.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Contact</Label>
              <Input className="mt-1.5 rounded-sm" value={whForm.contact} onChange={(e) => setWhForm({ ...whForm, contact: e.target.value })} placeholder="Manager name / phone" data-testid="warehouse-contact-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Notes</Label>
              <Textarea rows={2} className="mt-1.5 rounded-sm" value={whForm.notes} onChange={(e) => setWhForm({ ...whForm, notes: e.target.value })} data-testid="warehouse-notes-input" />
            </div>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={saveWh} disabled={busy} data-testid="save-warehouse-btn">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer dialog */}
      <Dialog open={!!transferItem} onOpenChange={(o) => !o && setTransferItem(null)}>
        <DialogContent data-testid="transfer-dialog">
          <DialogHeader><DialogTitle>Move {transferItem?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Currently in <b>{transferItem?.warehouse_name || "—"}</b> with <b>{transferItem?.quantity}</b> unit(s).
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wide">Quantity to move</Label>
              <Input
                type="number"
                min="1"
                max={transferItem?.quantity}
                className="mt-1.5 rounded-sm"
                value={transferQty}
                onChange={(e) => setTransferQty(e.target.value)}
                data-testid="transfer-qty-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Move part of the stock, or the full {transferItem?.quantity} unit(s).</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Destination warehouse</Label>
              <Select value={transferTo} onValueChange={setTransferTo}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="transfer-warehouse-select"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                <SelectContent>
                  {warehouses.filter((w) => w.id !== transferItem?.warehouse_id).map((w) => (
                    <SelectItem key={w.id} value={w.id} data-testid={`transfer-wh-option-${w.id}`}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={doTransfer} disabled={busy} data-testid="confirm-transfer-btn">{busy ? "Moving…" : "Move Stock"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movements ledger dialog */}
      <Dialog open={!!moveItem} onOpenChange={(o) => !o && setMoveItem(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="movements-dialog">
          <DialogHeader><DialogTitle>Movements — {moveItem?.name}</DialogTitle></DialogHeader>
          {moveItem && (
            <>
              {(moveItem.batches || []).some((b) => b.photos) && (
                <div className="space-y-1.5" data-testid="intake-photo-audit">
                  <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Intake photos (audit)</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {(moveItem.batches || []).filter((b) => b.photos).map((b, i) => (
                      <div key={i} className="border border-border rounded-sm p-2 text-xs space-y-1" data-testid={`intake-photo-batch-${i}`}>
                        <p className="font-medium">Batch {b.batch || "—"} · Exp {b.expiry ? fmtDate(b.expiry) : "—"} · {b.quantity} pcs</p>
                        <p className="text-muted-foreground">{b.receipt_number}{b.received_by ? ` · by ${b.received_by}` : ""}{b.received_at ? ` · ${fmtDate(b.received_at)}` : ""}</p>
                        <div className="flex gap-2">
                          {["front", "side"].filter((k) => b.photos[k]).map((k) => (
                            <button key={k} type="button" className="underline text-primary" onClick={() => downloadStoredFile(b.photos[k], `${moveItem.name}-${b.batch || "batch"}-${k}.jpg`)} data-testid={`intake-photo-${k}-${i}`}>{k === "front" ? "Front photo" : "Batch/expiry photo"}</button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="border border-border rounded-sm p-3"><p className="text-xs uppercase text-muted-foreground">Received</p><p className="text-lg font-bold text-emerald-700">{moveItem.received || 0}</p></div>
                <div className="border border-border rounded-sm p-3"><p className="text-xs uppercase text-muted-foreground">Sold</p><p className="text-lg font-bold text-blue-700">{moveItem.sold || 0}</p></div>
                <div className="border border-border rounded-sm p-3"><p className="text-xs uppercase text-muted-foreground">On hand</p><p className="text-lg font-bold">{moveItem.quantity}</p></div>
              </div>
              <div className="mt-3 border border-border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border/60">
                      <th className="px-3 py-2">When</th>
                      <th className="px-3 py-2">Movement</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2">Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...(moveItem.movements || [])].reverse().map((m, i) => {
                      const d = m.delta || 0;
                      const ref = m.ref || {};
                      return (
                        <tr key={i} className="border-b border-border/40 last:border-0" data-testid={`movement-row-${i}`}>
                          <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{fmtDateTime(m.ts)}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex items-center gap-1 text-xs font-semibold ${d < 0 ? "text-blue-700" : d > 0 ? "text-emerald-700" : "text-amber-700"}`}>
                              {d < 0 ? <ArrowDown size={12} /> : d > 0 ? <ArrowUp size={12} /> : <ArrowsLeftRight size={12} />} {m.event}
                            </span>
                            {m.by && <span className="text-xs text-muted-foreground ml-1">· {m.by}</span>}
                          </td>
                          <td className={`px-3 py-2 text-right font-semibold ${d < 0 ? "text-blue-700" : d > 0 ? "text-emerald-700" : "text-muted-foreground"}`}>{d > 0 ? `+${d}` : d === 0 ? "—" : d}</td>
                          <td className="px-3 py-2">
                            {ref.kind === "order" && ref.id ? (
                              <Link to={`/orders/${ref.id}`} className="text-primary hover:underline" data-testid={`movement-link-${ref.number}`}>{ref.number}</Link>
                            ) : ref.kind === "po" && ref.id ? (
                              <Link to={`/purchase-orders/${ref.id}`} className="text-primary hover:underline" data-testid={`movement-link-${ref.number}`}>{ref.number}</Link>
                            ) : ref.number ? (
                              <span className="text-muted-foreground">{ref.number}</span>
                            ) : <span className="text-muted-foreground">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                    {(moveItem.movements || []).length === 0 && (
                      <tr><td colSpan={4} className="px-3 py-8 text-center text-muted-foreground">No movements yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
